import { Button, Input } from '@breetometer/components';
import { classNameMerger } from '@breetometer/utils';
import { FiatIcon, MoneyBagIcon } from 'BreetAssets';
import { capitalizeFirstLetter, CURRENCYCONFIG, getMagnitudeText } from 'BreetHelpers';
import { type ChangeEvent, type MouseEvent, useCallback } from 'react';

import FiatDropDown from './components/FiatDropDown';
import type { FiatInputProps } from './FiatInput.helpers';

const FiatInput = ({
	id,
	value,
	onChange,
	label,
	icon,
	handleError,
	currencyCode,
	errorMessage,
	message,
	walletDetails,
	handleMaxClick,
	dropDown,
	hideTooltip,
	loading,
	wrapperClass,
	...rest
}: FiatInputProps) => {
	const handleInputValue = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			onChange?.(e);

			handleError?.(e);
		},
		[onChange, handleError]
	);

	const onInputClick = useCallback(
		(e: MouseEvent<HTMLInputElement>) => {
			if (String(value) === '0') {
				e.currentTarget.select();
			}
		},
		[value]
	);

	const currency = CURRENCYCONFIG[currencyCode].symbol;

	const tooltip = getMagnitudeText(value as string);

	const balanceCurrencyCode = !rest.disabled && walletDetails ? `Balance: ${walletDetails.currencyAmount}` : currencyCode;

	const inputMessage = errorMessage?.length ? errorMessage : message;

	const inputMessageClass = errorMessage ? 'fiat_input_error' : 'fiat_input_message';

	const isMaxFeat = typeof handleMaxClick === 'function';

	return (
		<div className={classNameMerger('fiat_input', wrapperClass)}>
			{!hideTooltip && !!tooltip && (
				<div className='fiat_tooltip'>
					<span>{tooltip}</span>
				</div>
			)}

			<div className={`fiat_input_wrapper border-dark-navy bg-primary-500 ${isMaxFeat ? '' : 'no-max'}`}>
				<label
					className='fiat_input_label color-neutral-50'
					htmlFor={id}
				>
					{label}
				</label>

				<div className='fiat_input_component_wrapper'>
					{!loading ? (
						<>
							<label
								htmlFor={id}
								className='fiat_input_component_wrapper_label color-neutral-50'
							>
								{currency}
							</label>
							<Input
								className='fiat_input_component theme-border-none'
								id=''
								label=''
								{...rest}
								onChange={handleInputValue}
								onClick={onInputClick}
								value={value}
								autoComplete='off'
								inputMode='numeric'
								pattern='^\d{1,3}(,\d{3})*(\.\d{0,2})?$'
							/>
						</>
					) : (
						<span className='global-skeleton'>loading...</span>
					)}

					<div className='fiat_input_balance bg-primary-400 border-dark-navy'>
						{!dropDown?.selectedOption?.label && !dropDown?.defaultOption?.label ? (
							<>
								{icon ?? <FiatIcon currencyCode={currencyCode} />}

								<p className='fiat_input_balance_amount color-neutral-50'>{balanceCurrencyCode}</p>
							</>
						) : (
							<FiatDropDown {...dropDown} />
						)}
					</div>
				</div>

				{isMaxFeat && (
					<div className='fiat_input_max'>
						<Button
							variant='native'
							type='button'
							onClick={handleMaxClick}
							className='border-dark-navy bg-primary-400 color-neutral-50'
						>
							<MoneyBagIcon className='svg-dark svg-dark-outline svg-dark-fill' />
							Max
						</Button>
					</div>
				)}
			</div>

			{!!inputMessage && <span className={inputMessageClass}>{capitalizeFirstLetter(inputMessage)}</span>}
		</div>
	);
};

export default FiatInput;
